import { useMemo, useState } from "react";
import type { ResultEnvelope } from "./types";
import { hasCardRenderer, renderCard } from "./App";
import { LabelsProvider, type WidgetLabels } from "./lib/labels";
import { showcaseEntries } from "./showcaseData";

const _SESSION_TOKEN = "widget-showcase";
const _VIEWPORTS = {
  desktop: { label: "Desktop (1280~)", min: 320, max: 520, default: 400 },
  tablet: { label: "Tablet (769~1279)", min: 320, max: 400, default: 370 },
} as const;

function stylePathFor(component: string) {
  const guideStyleComponents = new Set([
    "ai_top_picks",
    "compare_table",
    "concept_compare_table",
    "delivery_comparison_table",
    "promotion_list",
    "coupon_list",
    "my_coupon_list",
    "secret_coupon",
    "cart_added",
    "simple_product_carousel",
    "cart_confirm_preview",
    "find_my_model",
    "login_nudge",
  ]);
  if (guideStyleComponents.has(component)) {
    return "prototype/frontend/widget/preview/widget.css";
  }
  return "prototype/frontend/widget/preview/widget.legacy.css";
}

function shellClassFor(component: string) {
  if (component === "compare_table") return "ac-shell--white";
  if (["order_list", "track_my_orders_form"].includes(component)) {
    return "ac-shell--plain";
  }
  return "ac-shell";
}

export function WidgetShowcase() {
  const initialWidgetFilter = (() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("widget") || "";
  })();
  const [viewportMode, setViewportMode] = useState<keyof typeof _VIEWPORTS>("desktop");
  const [frameWidth, setFrameWidth] = useState<number>(_VIEWPORTS.desktop.default);
  const [query, setQuery] = useState(initialWidgetFilter);
  const [showPayloads, setShowPayloads] = useState(false);
  const [productDetailDraft, setProductDetailDraft] = useState<string>(() => {
    const entry = showcaseEntries.find((item) => item.id === "product-detail");
    return JSON.stringify(entry?.data ?? {}, null, 2);
  });
  const [productDetailApplied, setProductDetailApplied] = useState<ResultEnvelope["data"] | null>(() => {
    const entry = showcaseEntries.find((item) => item.id === "product-detail");
    return entry?.data ?? null;
  });
  const [productDetailError, setProductDetailError] = useState<string | null>(null);
  const [productDetailCopied, setProductDetailCopied] = useState(false);
  const viewport = _VIEWPORTS[viewportMode];
  const clampedWidth = useMemo(
    () => Math.min(viewport.max, Math.max(viewport.min, frameWidth)),
    [frameWidth, viewport.max, viewport.min],
  );
  const filteredEntries = useMemo(() => {
    const q = query.trim().toLowerCase();
    const visibleEntries = showcaseEntries.filter(
      (entry) => !entry.hidden && hasCardRenderer(entry.component),
    );
    if (!q) return visibleEntries;
    return visibleEntries.filter((entry) => {
      const haystack = `${entry.id} ${entry.title} ${entry.component} ${entry.note || ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [query]);

  const handleViewportMode = (mode: keyof typeof _VIEWPORTS) => {
    setViewportMode(mode);
    setFrameWidth(_VIEWPORTS[mode].default);
  };

  const applyProductDetailDraft = () => {
    try {
      const parsed = JSON.parse(productDetailDraft) as ResultEnvelope["data"];
      setProductDetailApplied(parsed);
      setProductDetailError(null);
    } catch (error) {
      setProductDetailError(error instanceof Error ? error.message : "Invalid JSON");
    }
  };

  const resetProductDetailDraft = () => {
    const entry = showcaseEntries.find((item) => item.id === "product-detail");
    const next = JSON.stringify(entry?.data ?? {}, null, 2);
    setProductDetailDraft(next);
    setProductDetailApplied(entry?.data ?? null);
    setProductDetailError(null);
    setProductDetailCopied(false);
  };

  const copyProductDetailDraft = async () => {
    try {
      await navigator.clipboard.writeText(productDetailDraft);
      setProductDetailCopied(true);
      window.setTimeout(() => setProductDetailCopied(false), 1500);
    } catch {
      setProductDetailCopied(false);
    }
  };

  return (
    <div style={{ background: "#fafaf9", minHeight: "100vh", padding: "24px" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <header style={{ marginBottom: "20px" }}>
          <h1 style={{ fontSize: "28px", fontWeight: 700, marginBottom: "8px" }}>
            Actual Widget Showcase
          </h1>
          <p style={{ color: "#6b7280", fontSize: "14px", lineHeight: 1.6 }}>
            This page renders the real React widget components from <code>src/cards/</code>,
            not the static HTML mockups.
          </p>
        </header>

        <section
          style={{
            marginBottom: "20px",
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: "16px",
            padding: "16px",
            position: "sticky",
            top: "12px",
            zIndex: 25,
            boxShadow: "0 10px 30px rgba(17, 24, 39, 0.06)",
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center", marginBottom: "12px" }}>
            <div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>Review controls</div>
            <div style={{ fontSize: "12px", color: "#6b7280" }}>
              Showing <strong>{filteredEntries.length}</strong> of <strong>{showcaseEntries.length}</strong>
            </div>
            <button
              type="button"
              onClick={() => setShowPayloads((prev) => !prev)}
              style={{
                marginLeft: "auto",
                border: "1px solid #d1d5db",
                background: "#fff",
                color: "#374151",
                borderRadius: "9999px",
                padding: "8px 12px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {showPayloads ? "Hide all payloads" : "Show all payloads"}
            </button>
          </div>

          <div style={{ maxWidth: "420px", marginBottom: "12px" }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by component, title, or ?widget="
              style={{
                width: "100%",
                border: "1px solid #d1d5db",
                borderRadius: "10px",
                padding: "10px 12px",
                fontSize: "13px",
                color: "#111827",
                background: "#fafafa",
              }}
            />
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "12px" }}>
            {(Object.keys(_VIEWPORTS) as Array<keyof typeof _VIEWPORTS>).map((mode) => {
              const active = mode === viewportMode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => handleViewportMode(mode)}
                  style={{
                    border: active ? "1px solid #111827" : "1px solid #d1d5db",
                    background: active ? "#111827" : "#fff",
                    color: active ? "#fff" : "#374151",
                    borderRadius: "9999px",
                    padding: "8px 12px",
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {_VIEWPORTS[mode].label}
                </button>
              );
            })}
          </div>

          <div style={{ display: "grid", gap: "8px", maxWidth: "420px" }}>
            <div style={{ fontSize: "12px", color: "#4b5563" }}>
              Frame width: <strong>{clampedWidth}px</strong>
              {` `}
              <span style={{ color: "#9ca3af" }}>
                (default {viewport.default}px / min {viewport.min}px / max {viewport.max}px)
              </span>
            </div>
            <input
              type="range"
              min={viewport.min}
              max={viewport.max}
              value={clampedWidth}
              onChange={(e) => setFrameWidth(Number(e.target.value))}
            />
          </div>
        </section>

        <nav
          style={{
            position: "sticky",
            top: 0,
            zIndex: 20,
            background: "rgba(250,250,249,.92)",
            backdropFilter: "blur(8px)",
            borderBottom: "1px solid #e5e7eb",
            padding: "12px 0",
            marginBottom: "24px",
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {filteredEntries.map((entry) => (
              <a
                key={entry.id}
                href={`#${entry.id}`}
                style={{
                  fontSize: "12px",
                  padding: "4px 10px",
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "9999px",
                  color: "#374151",
                  textDecoration: "none",
                }}
              >
                {entry.component}
              </a>
            ))}
          </div>
        </nav>

        <div style={{ display: "grid", gap: "32px" }}>
          {filteredEntries.map((entry) => {
            const currentData =
              entry.id === "product-detail" && productDetailApplied
                ? productDetailApplied
                : entry.data;
            const env = {
              component: entry.component,
              data: currentData,
            } as ResultEnvelope;

            return (
              <section
                key={entry.id}
                id={entry.id}
                style={{
                  scrollMarginTop: "140px",
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "20px",
                  padding: "18px",
                  boxShadow: "0 16px 40px rgba(17, 24, 39, 0.05)",
                }}
              >
                <div style={{ marginBottom: "14px", display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                  <div style={{ fontSize: "18px", fontWeight: 700, color: "#111827" }}>{entry.title}</div>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#7c3aed",
                      background: "#f5f3ff",
                      border: "1px solid #ddd6fe",
                      borderRadius: "9999px",
                      padding: "4px 8px",
                    }}
                  >
                    {entry.component}
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#0f766e",
                      background: "#ecfeff",
                      border: "1px solid #a5f3fc",
                      borderRadius: "9999px",
                      padding: "4px 8px",
                    }}
                  >
                    {viewport.label} · {clampedWidth}px
                  </span>
                  <a
                    href={`#${entry.id}`}
                    style={{ marginLeft: "auto", fontSize: "12px", color: "#6b7280", textDecoration: "none" }}
                  >
                    #{entry.id}
                  </a>
                  <div style={{ width: "100%", fontSize: "12px", color: "#9ca3af" }}>
                    {entry.note || "Actual renderer preview with sample payload."}
                  </div>
                  <div
                    style={{
                      width: "100%",
                      display: "grid",
                      gap: "6px",
                      marginTop: "4px",
                      padding: "10px 12px",
                      background: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      fontSize: "12px",
                    }}
                  >
                    <div style={{ color: "#4b5563" }}>
                      Edit component: <code>{entry.rendererPath}</code>
                    </div>
                    <div style={{ color: "#4b5563" }}>
                      Main styles: <code>{stylePathFor(entry.component)}</code>
                    </div>
                    <div style={{ color: "#4b5563" }}>
                      Showcase payload: <code>prototype/frontend/widget/src/showcaseData.ts</code>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gap: "16px",
                    alignItems: "start",
                    gridTemplateColumns:
                      entry.id === "product-detail"
                        ? "minmax(0, 1fr) minmax(320px, 380px)"
                        : "minmax(0, 1fr)",
                  }}
                >
                  <div
                    style={{
                      width: `${clampedWidth}px`,
                      maxWidth: "100%",
                      border: "1px dashed #d1d5db",
                      borderRadius: "16px",
                      padding: "12px",
                      // 실제 NCMS 챗 패널 배경색(#f0ece4)으로 카드 스테이지를 맞춰, 쿠폰 notch 등
                      // 배경 의존 효과(투명하게 잘린 반원)가 미리보기에서도 실제와 동일하게 보이게 한다.
                      background: "#f0ece4",
                    }}
                  >
                    <div className={shellClassFor(entry.component)}>
                      <LabelsProvider
                        labels={(currentData as { labels?: WidgetLabels }).labels}
                      >
                        {renderCard(entry.component, env, _SESSION_TOKEN)}
                      </LabelsProvider>
                    </div>
                  </div>

                  {entry.id === "product-detail" ? (
                    <aside
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: "16px",
                        background: "#fff",
                        padding: "14px",
                        boxShadow: "0 8px 24px rgba(17, 24, 39, 0.05)",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                        <strong style={{ fontSize: "13px", color: "#111827" }}>Product Detail Payload Editor</strong>
                        <span style={{ fontSize: "11px", color: "#6b7280" }}>draft only</span>
                      </div>
                      <textarea
                        value={productDetailDraft}
                        onChange={(e) => {
                          setProductDetailDraft(e.target.value);
                          if (productDetailError) setProductDetailError(null);
                        }}
                        spellCheck={false}
                        style={{
                          width: "100%",
                          minHeight: "360px",
                          padding: "12px",
                          borderRadius: "12px",
                          border: "1px solid #d1d5db",
                          background: "#0f172a",
                          color: "#e5e7eb",
                          fontSize: "12px",
                          lineHeight: 1.5,
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                          resize: "vertical",
                          boxSizing: "border-box",
                        }}
                      />
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "10px" }}>
                        <button
                          type="button"
                          onClick={applyProductDetailDraft}
                          style={{
                            border: "1px solid #111827",
                            background: "#111827",
                            color: "#fff",
                            borderRadius: "9999px",
                            padding: "8px 12px",
                            fontSize: "12px",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          Apply
                        </button>
                        <button
                          type="button"
                          onClick={resetProductDetailDraft}
                          style={{
                            border: "1px solid #d1d5db",
                            background: "#fff",
                            color: "#374151",
                            borderRadius: "9999px",
                            padding: "8px 12px",
                            fontSize: "12px",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          onClick={() => void copyProductDetailDraft()}
                          style={{
                            border: "1px solid #d1d5db",
                            background: "#fff",
                            color: "#374151",
                            borderRadius: "9999px",
                            padding: "8px 12px",
                            fontSize: "12px",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          {productDetailCopied ? "Copied" : "Copy JSON"}
                        </button>
                      </div>
                      <div style={{ marginTop: "10px", fontSize: "12px", color: productDetailError ? "#b91c1c" : "#6b7280" }}>
                        {productDetailError
                          ? `JSON error: ${productDetailError}`
                          : "Edit the draft JSON and click Apply to rerender the Product Detail card."}
                      </div>
                    </aside>
                  ) : null}
                </div>

                <details
                  style={{
                    marginTop: "12px",
                    width: `${clampedWidth}px`,
                    maxWidth: "100%",
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    overflow: "hidden",
                  }}
                  open={showPayloads}
                >
                  <summary
                    style={{
                      cursor: "pointer",
                      padding: "12px 14px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#374151",
                      userSelect: "none",
                    }}
                  >
                    Payload JSON
                  </summary>
                  <pre
                    style={{
                      margin: 0,
                      padding: "0 14px 14px",
                      fontSize: "12px",
                      lineHeight: 1.5,
                      color: "#111827",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowX: "auto",
                    }}
                  >
                    {JSON.stringify(currentData, null, 2)}
                  </pre>
                </details>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
